// TrainersLayer.js
import React, { useState, useEffect } from "react";
import { shrinkRectOverlap } from "./collision";  // for your busy-tile collision check

/**
 * trainers: [{ trainer_id, trainer_name, x, y, sprite_sheet, wander }]
 * camX, camY: camera offset
 * playerX, playerY: world coords of the player
 * onTrainerClick: (trainer) => void
 */
export default function TrainersLayer({
  trainers,
  camX,
  camY,
  playerX,
  playerY,
  onTrainerClick,
  zBase,
}) {
  const [state, setState] = useState(() =>
    trainers.map(t => ({
      ...t,
      dirIndex: 0,
      moved: 0,
      step: 0,
    }))
  );

  useEffect(() => {
    setState(trainers.map(t => ({
      ...t,
      dirIndex: 0,
      moved:    0,
      step:     0,
    })));
  }, [trainers]);

  useEffect(() => {
    const SIDE     = 64,
          SPEED    = 2,
          INTERVAL = 200,
          DIRS     = [
            { dx:  1, dy:  0 },
            { dx:  0, dy:  1 },
            { dx: -1, dy:  0 },
            { dx:  0, dy: -1 },
          ];

    const iv = setInterval(() => {
      setState(prev =>
        prev.map(tr => {
          // If wonder is 0, trainer stays still (no animation)
          if (Number(tr.wander) === 0) {
            // Keep static trainers at step 0 to prevent wiggling
            return { ...tr, step: 0 };
          }
          
          // If wonder is not 1, don't update
          if (Number(tr.wander) !== 1) return tr;

          let { x, y, dirIndex, moved, step } = tr;
          const nx = x + DIRS[dirIndex].dx * SPEED;
          const ny = y + DIRS[dirIndex].dy * SPEED;

          // if they'd overlap the player, only animate in place
          if (shrinkRectOverlap(nx, ny, 32, 32, playerX, playerY, 32, 32, 0,0,0,0)) {
            return { ...tr, step: (step + 1) % 3 };
          }

          // otherwise move
          x     = nx;
          y     = ny;
          moved = moved + SPEED;
          if (moved >= SIDE) {
            moved    = 0;
            dirIndex = (dirIndex + 1) % 4;
          }
          step = (step + 1) % 3;
          return { ...tr, x, y, dirIndex, moved, step };
        })
      );
    }, INTERVAL);

    return () => clearInterval(iv);
  }, []); // Removed playerX and playerY from dependencies to keep animations running

  // sheet: 3 cols × 4 rows → row 0=down,1=right,2=up,3=left
  const rowMap = [1,0,3,2];

  return (
    <>
      {state.map(tr => {
        const isStatic = Number(tr.wander) === 0;
        
        // For static trainers, use different dimensions (16x24)
        const width = isStatic ? 16 : 32;
        const height = isStatic ? 24 : 32;
        
        // Calculate background position based on trainer type
        const frame = tr.step;
        const row = rowMap[tr.dirIndex];
        const bgX = -frame * width;
        const bgY = -row * height;

        const playerFeetY = (Number(playerY) || 0) + 32;
        const trainerFeetY = (Number(tr.y) || 0) + height;
        const trainerZ = typeof zBase === "number" && Number.isFinite(zBase)
          ? (Number(zBase) + Math.round(Number(trainerFeetY) || 0))
          : (playerFeetY > trainerFeetY ? 6 : 8);

        return (
          <div
            key={tr.owned_npc_id || tr.instance_id || tr.trainer_id}
            className={`trainer ${isStatic ? 'trainer-static' : ''}`}
            onClick={() => onTrainerClick?.(tr)}
            style={{
              position:           "absolute",
              width:              width,
              height:             height,
              left:               tr.x - camX,
              top:                tr.y - camY,
              backgroundImage:    `url(${tr.sprite_sheet})`,
              backgroundPosition: `${bgX}px ${bgY}px`,
              cursor:             "pointer",
              zIndex:             trainerZ,
            }}
          />
        );
      })}
    </>
  );
}
